import { useState } from "react";
import { NotebookPen } from "lucide-react";
import { TYPE_STYLE, subjectColor } from "../../lib/timetable";
import { useClock, fmtTime } from "../../lib/clockFormat";
import { useIsMobile, defaultDay } from "../../lib/useIsMobile";
import { DaySwitcher } from "../DaySwitcher";

export function TimetableGrid({ days, slots, dayOverrides, entries, notes, subscribed, onSlot, onNote }) {
  const h24 = useClock();
  const isMobile = useIsMobile();
  const [mobileDay, setMobileDay] = useState("");
  const activeDay = days.includes(mobileDay) ? mobileDay : defaultDay(days);
  const shownDays = isMobile ? [activeDay] : days;
  const daySlotsList = shownDays.map((d) => dayOverrides[d]?.slots || slots);
  const rowCount = Math.max(slots.length, ...daySlotsList.map((s) => s.length));

  return (
    <>
      {isMobile && <DaySwitcher days={days} active={activeDay} onChange={setMobileDay} testPrefix="grid" />}
      <div className={`nb-card overflow-hidden ${!subscribed ? "opacity-60 pointer-events-none select-none" : ""}`} data-testid="parent-timetable-wrap">
        <div className="overflow-x-auto">
          <table className={`w-full border-collapse ${isMobile ? "" : "min-w-[720px]"}`} data-testid="parent-timetable">
            <thead>
              <tr>
                <th className="border-b-2 border-r-2 border-zinc-900 bg-zinc-900 text-white text-xs font-bold p-3 text-left w-28 sticky left-0">Time</th>
                {shownDays.map((d) => (
                  <th key={d} className="border-b-2 border-zinc-900 bg-zinc-900 text-white p-3">
                    <div className="font-bold text-sm">{d}</div>
                    <button onClick={() => onNote(d)}
                      data-testid={`parent-note-btn-${d.toLowerCase()}`}
                      className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-lg border-2 transition-colors duration-150 ${notes[d] ? "bg-[#FDE68A] border-zinc-900 text-zinc-900" : "border-white/40 text-white/80 hover:bg-white/10"}`}>
                      <NotebookPen className="w-3 h-3" /> {notes[d] ? "Note" : "Add note"}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowCount }).map((_, idx) => {
                const slot = isMobile ? (daySlotsList[0] || slots)[idx] : slots[idx];
                return (
                <tr key={idx}>
                  <td className="border-r-2 border-b-2 border-zinc-900 bg-white p-3 sticky left-0 z-10">
                    <div className="font-bold text-sm">{slot ? slot.label : "—"}</div>
                    <div className="text-xs text-zinc-500 font-semibold">{slot ? `${fmtTime(slot.start, h24)}–${fmtTime(slot.end, h24)}` : ""}</div>
                  </td>
                  {shownDays.map((day, di) => {
                    const dSlot = daySlotsList[di][idx];
                    if (!dSlot) {
                      return <td key={day} className="border-b-2 border-zinc-300 p-1.5 bg-zinc-100" data-testid={`parent-slot-${day.toLowerCase()}-${idx}`} />;
                    }
                    if (dSlot.type !== "period") {
                      return (
                        <td key={day} className="border-b-2 border-zinc-300 p-2 text-center text-xs font-bold text-zinc-500"
                          style={{ backgroundColor: TYPE_STYLE[dSlot.type] }} data-testid={`parent-slot-${day.toLowerCase()}-${idx}`}>
                          {dSlot.label}
                        </td>
                      );
                    }
                    const entry = entries[`${day}:${idx}`];
                    return (
                      <td key={day} className="border-b-2 border-zinc-300 p-1.5">
                        <button onClick={() => onSlot(day, idx)} data-testid={`parent-timetable-slot-${day.toLowerCase()}-${idx}`}
                          className={`w-full min-h-[64px] rounded-lg border-2 p-2 text-left transition-[transform,box-shadow] duration-150 hover:scale-[1.02] ${entry ? "border-zinc-900 shadow-[2px_2px_0px_0px_#18181B]" : "border-dashed border-zinc-300 bg-zinc-50 hover:border-zinc-900 hover:bg-white"}`}
                          style={entry?.subject ? { backgroundColor: subjectColor(entry.subject) } : {}}>
                          {dayOverrides[day] && <div className="text-[9px] font-black text-zinc-400 mb-0.5">{dSlot.label} · {fmtTime(dSlot.start, h24)}–{fmtTime(dSlot.end, h24)}</div>}
                          {entry ? (
                            <>
                              <div className="font-display font-bold text-sm leading-tight">{entry.subject || "Class"}</div>
                              <div className="text-[11px] font-semibold text-zinc-700 mt-0.5 leading-tight">
                                {[entry.teacher, entry.room].filter(Boolean).join(" · ")}
                              </div>
                            </>
                          ) : (
                            <span className="text-xs font-bold text-zinc-400">+ Add</span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-zinc-500 font-semibold mt-3">Tap any period to type in the subject, teacher and room yourself — no school database needed.</p>
    </>
  );
}
